import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import styled from 'styled-components';

import { FlexControllerV2 } from 'components/FlexControllerInput/V2';

const StyledDropdownMenu = styled(DropdownMenu)`
  width: 350px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ScrollableContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }
`;

const SectionHeader = styled.div`
  padding: 12px 16px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.9rem;
  color: #333;

  &:hover {
    background-color: #f9f9f9;
  }
`;

const CheckboxSquare = styled.div`
  width: 18px;
  height: 18px;
  border: 1px solid ${(props) => (props.checked ? '#e91e63' : '#ccc')};
  background-color: ${(props) => (props.checked ? '#e91e63' : '#fff')};
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &:after {
    content: '';
    display: ${(props) => (props.checked ? 'block' : 'none')};
    width: 5px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-top: -2px;
  }
`;

const DropdownChatChannelSelector = ({
  channels = [],
  selectedChannels: rawSelectedChannels,
  onChange,
  iconColor = 'inherit'
}) => {
  const selectedChannels = rawSelectedChannels || [];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { control, watch } = useForm({
    defaultValues: {
      search: ''
    }
  });

  const searchTerm = watch('search');

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const filteredChannels = useMemo(() => {
    if (!searchTerm) return channels;
    return channels.filter((channel) =>
      channel.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [channels, searchTerm]);

  const handleChannelClick = (channel) => {
    const idString = String(channel.value);
    const isSelected = selectedChannels.includes(idString);
    let newSelection = [];

    if (isSelected) {
      newSelection = selectedChannels.filter((id) => id !== idString);
    } else {
      newSelection = [...selectedChannels, idString];
    }

    if (onChange) {
      onChange(newSelection);
    }
  };

  let displayLabel = 'Selecione...';
  let displayIcon = 'fa-comments';

  const isAllSelected =
    selectedChannels.length > 0 &&
    channels.length > 0 &&
    selectedChannels.length === channels.length;

  if (isAllSelected) {
    displayLabel = 'Todos os Canais';
    displayIcon = 'fa-comments';
  } else if (selectedChannels.length > 0) {
    displayLabel = `Canais (${selectedChannels.length})`;
    displayIcon = 'fa-comments';
  } else {
    displayLabel = 'Selecione os canais';
  }

  return (
    <Dropdown isOpen={isDropdownOpen} toggle={toggleDropdown} direction="down">
      <DropdownToggle
        tag="div"
        style={{ cursor: 'pointer', color: iconColor, fontWeight: 600 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className={`fas ${displayIcon}`} />
          <span>{displayLabel}</span>
          <i className="fas fa-chevron-down" style={{ fontSize: '0.8rem' }} />
        </div>
      </DropdownToggle>

      <StyledDropdownMenu right>
        <HeaderContainer>
          <div style={{ width: '100%' }}>
            <InputGroup size="sm">
              <InputGroupAddon addonType="prepend">
                <InputGroupText
                  style={{ backgroundColor: '#fff', borderRight: 'none' }}
                >
                  <i className="fas fa-search text-muted" />
                </InputGroupText>
              </InputGroupAddon>
              <div style={{ flex: 1 }}>
                <FlexControllerV2
                  type="text"
                  name="search"
                  control={control}
                  placeholder="Buscar..."
                  style={{
                    boxShadow: 'none',
                    height: '31px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            </InputGroup>
          </div>
        </HeaderContainer>

        <ScrollableContent>
          <SectionHeader>CANAIS DE CHAT</SectionHeader>

          {channels.length > 0 && (
            <>
              <ListItem
                onClick={() => {
                  if (isAllSelected) {
                    onChange && onChange([]);
                  } else {
                    onChange && onChange(channels.map((c) => String(c.value)));
                  }
                }}
              >
                <CheckboxSquare checked={isAllSelected} />
                <span style={{ fontWeight: 600 }}>Todos</span>
              </ListItem>
            </>
          )}
          {filteredChannels.length === 0 ? (
            <div className="text-center text-muted py-2">
              <small>Nenhum canal encontrado</small>
            </div>
          ) : (
            filteredChannels.map((channel) => {
              const isChecked = selectedChannels.includes(
                String(channel.value)
              );
              return (
                <ListItem
                  key={channel.value}
                  onClick={() => handleChannelClick(channel)}
                >
                  <CheckboxSquare checked={isChecked} />
                  <span>{channel.label}</span>
                </ListItem>
              );
            })
          )}
        </ScrollableContent>
      </StyledDropdownMenu>
    </Dropdown>
  );
};

export default DropdownChatChannelSelector;
